import { useEffect, useState } from 'react'
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Center,
  Divider,
  Drawer,
  Group,
  Loader,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconEdit, IconPlus, IconTrash } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import type { ContactRole, ProjectContactDto } from '@ledokol/contracts'
import { createContact, deleteContact, fetchContacts, updateContact } from '../../shared/api/project-crm'
import { DateOnlyPicker } from './date-only-picker'

const ROLE_OPTS: { value: ContactRole; label: string; color: string }[] = [
  { value: 'decision_maker', label: 'ЛПР', color: 'red' },
  { value: 'cmo', label: 'CMO', color: 'grape' },
  { value: 'marketer', label: 'Маркетолог', color: 'blue' },
  { value: 'procurement', label: 'Закупки', color: 'orange' },
  { value: 'finance', label: 'Финансы', color: 'green' },
  { value: 'legal', label: 'Юристы', color: 'gray' },
  { value: 'other', label: 'Другое', color: 'dark' },
]

const ROLE_LABEL: Record<ContactRole, string> = Object.fromEntries(
  ROLE_OPTS.map((r) => [r.value, r.label]),
) as Record<ContactRole, string>

const ROLE_COLOR: Record<ContactRole, string> = Object.fromEntries(
  ROLE_OPTS.map((r) => [r.value, r.color]),
) as Record<ContactRole, string>

export function ContactsTab({ projectId }: { projectId: string }) {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['contacts', projectId],
    queryFn: () => fetchContacts(projectId),
  })

  const [editOpen, setEditOpen] = useState(false)
  const [editing, setEditing] = useState<ProjectContactDto | null>(null)

  // Форма (используется для create + edit, по флагу editing.id)
  const [fullName, setFullName] = useState('')
  const [role, setRole] = useState<ContactRole | null>(null)
  const [position, setPosition] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [birthDate, setBirthDate] = useState('')
  const [notes, setNotes] = useState('')

  useEffect(() => {
    if (editing) {
      setFullName(editing.full_name)
      setRole(editing.contact_role)
      setPosition(editing.position ?? '')
      setPhone(editing.phone ?? '')
      setEmail(editing.email ?? '')
      setBirthDate(editing.birth_date ?? '')
      setNotes(editing.notes ?? '')
    } else if (editOpen) {
      // Очистка для нового
      setFullName('')
      setRole(null)
      setPosition('')
      setPhone('')
      setEmail('')
      setBirthDate('')
      setNotes('')
    }
  }, [editing, editOpen])

  const saveMut = useMutation({
    mutationFn: async () => {
      const payload = {
        full_name: fullName,
        contact_role: role!,
        position: position || null,
        phone: phone || null,
        email: email || null,
        birth_date: birthDate || null,
        notes: notes || null,
      }
      if (editing) {
        return updateContact(projectId, editing.id, payload)
      }
      return createContact(projectId, payload)
    },
    onSuccess: () => {
      notifications.show({ message: editing ? 'Контакт обновлён' : 'Контакт создан', color: 'green' })
      setEditOpen(false)
      setEditing(null)
      qc.invalidateQueries({ queryKey: ['contacts', projectId] })
      qc.invalidateQueries({ queryKey: ['birthdays'] })
    },
    onError: (e: unknown) => {
      const msg = (e instanceof Error && e.message) || 'Не удалось'
      notifications.show({ message: msg, color: 'red' })
    },
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteContact(projectId, id),
    onSuccess: () => {
      notifications.show({ message: 'Контакт удалён', color: 'green' })
      qc.invalidateQueries({ queryKey: ['contacts', projectId] })
      qc.invalidateQueries({ queryKey: ['birthdays'] })
    },
  })

  if (isLoading) {
    return (
      <Center mih={200}>
        <Loader />
      </Center>
    )
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text c="dimmed" size="sm">
          Контакты клиента: ЛПР, маркетологи, юристы. Хранятся со ссылкой на проект,
          доступны всем участникам команды.
        </Text>
        <Button leftSection={<IconPlus size={16} />} onClick={() => { setEditing(null); setEditOpen(true) }}>
          Добавить контакт
        </Button>
      </Group>

      {data?.length === 0 ? (
        <Center mih={100}>
          <Text c="dimmed">Контактов пока нет</Text>
        </Center>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>ФИО</Table.Th>
              <Table.Th>Роль</Table.Th>
              <Table.Th>Должность</Table.Th>
              <Table.Th>Контакты</Table.Th>
              <Table.Th>ДР</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data?.map((c) => (
              <Table.Tr key={c.id}>
                <Table.Td>{c.full_name}</Table.Td>
                <Table.Td>
                  <Badge color={ROLE_COLOR[c.contact_role]} variant="light">
                    {ROLE_LABEL[c.contact_role]}
                  </Badge>
                </Table.Td>
                <Table.Td>{c.position ?? '—'}</Table.Td>
                <Table.Td>
                  {c.phone && (
                    <Text size="sm" component="a" href={`tel:${c.phone}`} c="blue">
                      {c.phone}
                    </Text>
                  )}
                  {c.phone && c.email && <br />}
                  {c.email && (
                    <Text size="sm" component="a" href={`mailto:${c.email}`} c="blue">
                      {c.email}
                    </Text>
                  )}
                  {!c.phone && !c.email && <Text size="sm" c="dimmed">—</Text>}
                </Table.Td>
                <Table.Td>{c.birth_date ? dayjs(c.birth_date).format('D MMM') : '—'}</Table.Td>
                <Table.Td>
                  <Group gap={4} wrap="nowrap">
                    <ActionIcon variant="subtle" onClick={() => { setEditing(c); setEditOpen(true) }} title="Редактировать">
                      <IconEdit size={16} />
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => {
                        if (confirm(`Удалить контакт «${c.full_name}»?`)) {
                          deleteMut.mutate(c.id)
                        }
                      }}
                      title="Удалить"
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <Drawer
        opened={editOpen}
        onClose={() => { setEditOpen(false); setEditing(null) }}
        title={editing ? `Редактирование: ${editing.full_name}` : 'Новый контакт'}
        position="right"
        size="md"
      >
        <Stack>
          <TextInput label="ФИО" value={fullName} onChange={(e) => setFullName(e.currentTarget.value)} required />
          <Select
            label="Роль"
            data={ROLE_OPTS.map((r) => ({ value: r.value, label: r.label }))}
            value={role}
            onChange={(v) => setRole(v as ContactRole | null)}
            required
          />
          <TextInput
            label="Должность (свободный текст)"
            value={position}
            onChange={(e) => setPosition(e.currentTarget.value)}
            placeholder="Brand Manager Coca-Cola"
          />
          <Group grow>
            <TextInput label="Телефон" value={phone} onChange={(e) => setPhone(e.currentTarget.value)} placeholder="+998 90 123 45 67" />
            <TextInput label="Email" type="email" value={email} onChange={(e) => setEmail(e.currentTarget.value)} />
          </Group>
          <DateOnlyPicker
            label="Дата рождения"
            value={birthDate || null}
            onChange={(v) => setBirthDate(v ?? '')}
            description="Для напоминания на дашборде «Ближайшие ДР»"
          />
          <Textarea label="Заметки" value={notes} onChange={(e) => setNotes(e.currentTarget.value)} autosize minRows={2} />
          <Divider />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => { setEditOpen(false); setEditing(null) }}>Отмена</Button>
            <Button
              loading={saveMut.isPending}
              disabled={!fullName.trim() || !role}
              onClick={() => saveMut.mutate()}
            >
              {editing ? 'Сохранить' : 'Создать'}
            </Button>
          </Group>
        </Stack>
      </Drawer>

      <Box mt="md">
        <Text size="xs" c="dimmed">
          Всего контактов: {data?.length ?? 0}
        </Text>
      </Box>
    </Stack>
  )
}
